import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from 'components/common'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import {
  initiateInventoryCronManual,
  initiateMasterDataCronManual,
  initiatePricingCronManual,
  syncAggregatorCron,
} from 'utils/api/configApi'
import { ManualCronFields } from './utils'
import { manualCronSchema } from 'utils/schema/configurationsSchema'
import { DateTime } from 'components/common/input/dateTime'
import { AxiosError } from 'axios'
import { ERROR_CODES } from 'utils/constants'

const loadingTypes = {
  MASTER: 'MASTER',
  INVENTORY: 'INVENTORY',
  PRICING: 'PRICING',
  AGGREGATOR: 'AGGREGATOR',
}

const successMessage =
  'Cron trigger successful. You will be notified after completion.'

const failedMessage = 'Failed to trigger cron'

const ManualCrons = () => {
  const [loading, setLoading] = useState({
    type: loadingTypes.MASTER,
    isLoading: false,
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ManualCronFields>({
    resolver: yupResolver(manualCronSchema),
  })

  const triggerMasterData = async ({
    lastUpdatedDateTime,
  }: ManualCronFields) => {
    setLoading({ type: loadingTypes.MASTER, isLoading: true })
    const triggerToast = toast.loading(`Triggering Master data cron..`)
    try {
      await initiateMasterDataCronManual(lastUpdatedDateTime)
      toast.success(successMessage, { id: triggerToast })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: triggerToast,
            duration: 3000,
          })
        }

        const msg = error?.response?.data?.errors?.msg

        if (msg === ERROR_CODES.FTP_ALREADY_RUNNING) {
          return toast.error('FTP generation is already in progress', {
            id: triggerToast,
            duration: 3000,
          })
        }
      }
      toast.error(failedMessage, { id: triggerToast })
    } finally {
      setLoading({ type: loadingTypes.MASTER, isLoading: false })
    }
  }

  const triggerInventory = async ({
    lastUpdatedDateTime,
  }: ManualCronFields) => {
    setLoading({ type: loadingTypes.INVENTORY, isLoading: true })
    const triggerToast = toast.loading(`Triggering Inventory cron..`)
    try {
      await initiateInventoryCronManual(lastUpdatedDateTime)
      toast.success(successMessage, { id: triggerToast })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: triggerToast,
            duration: 3000,
          })
        }

        const msg = error?.response?.data?.errors?.msg

        if (msg === ERROR_CODES.FTP_ALREADY_RUNNING) {
          return toast.error('FTP generation is already in progress', {
            id: triggerToast,
            duration: 3000,
          })
        }
      }
      toast.error(failedMessage, { id: triggerToast })
    } finally {
      setLoading({ type: loadingTypes.INVENTORY, isLoading: false })
    }
  }

  const triggerPricing = async () => {
    setLoading({ type: loadingTypes.PRICING, isLoading: true })
    const triggerToast = toast.loading(`Triggering pricing cron..`)
    try {
      await initiatePricingCronManual()
      toast.success(successMessage, { id: triggerToast })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: triggerToast,
            duration: 3000,
          })
        }
      }
      toast.error(failedMessage, { id: triggerToast })
    } finally {
      setLoading({ type: loadingTypes.PRICING, isLoading: false })
    }
  }

  const syncAggregators = async () => {
    setLoading({ type: loadingTypes.AGGREGATOR, isLoading: true })
    const triggerToast = toast.loading(`Initiating Aggregators syncing job...`)
    try {
      await syncAggregatorCron()
      toast.success('Intiated Aggregators syncing job', { id: triggerToast })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: triggerToast,
            duration: 3000,
          })
        }
      }
      toast.error('Failed to intiate aggregators syncing job', {
        id: triggerToast,
      })
    } finally {
      setLoading({ type: loadingTypes.AGGREGATOR, isLoading: false })
    }
  }

  return (
    <>
      <p className="mb-1 mt-3 text-sm font-bold text-sidebar-primary">
        Manual crons
      </p>

      <p className="mb-2 text-[11px] text-gray-500">
        Manually trigger the cron job for updating master data, inventory and
        pricing.
      </p>

      <div className="mb-3 w-full mid-md:w-1/3 mid-md:pr-3">
        <DateTime
          required
          type="datetime-local"
          label="ERP Last Updated Date Time"
          name={'lastUpdatedDateTime'}
          register={register}
          error={errors.lastUpdatedDateTime?.message}
          placeholder="ERP Last Updated Date Time"
        />
      </div>

      <div className="flex flex-wrap items-center gap-5">
        <Button
          buttonText="Trigger Master data"
          size="small"
          className="w-full max-w-36 !border !py-1.5 !font-normal"
          type="button"
          variant="outline"
          onClick={handleSubmit(triggerMasterData)}
          disabled={loading.isLoading}
          loading={loading.type === loadingTypes.MASTER && loading.isLoading}
        />

        <Button
          size="small"
          buttonText="Trigger Inventory"
          className="w-full max-w-36 !border !py-1.5 !font-normal"
          type="button"
          variant="outline"
          onClick={handleSubmit(triggerInventory)}
          disabled={loading.isLoading}
          loading={loading.type === loadingTypes.INVENTORY && loading.isLoading}
        />

        <Button
          size="small"
          buttonText="Trigger Pricing"
          className="w-full max-w-36 !border !py-1.5 !font-normal"
          type="button"
          variant="outline"
          onClick={triggerPricing}
          disabled={loading.isLoading}
          loading={loading.type === loadingTypes.PRICING && loading.isLoading}
        />

        <Button
          size="small"
          buttonText="Sync Aggregators in Pricing"
          className="w-full max-w-max !border !py-1.5 !font-normal"
          type="button"
          variant="outline"
          onClick={syncAggregators}
          disabled={loading.isLoading}
          loading={
            loading.type === loadingTypes.AGGREGATOR && loading.isLoading
          }
        />
      </div>
      <p className="mt-2 text-[11px] text-gray-500">
        Note: Select a date and time within the last year for ERP Last Updated
        Date Time.
      </p>
    </>
  )
}

export default ManualCrons
